import { QueryClient } from '@tanstack/react-query';
import {
  CACHE_DELETE_TIMEOUT_MS,
  clearAllStores,
  deleteOfflineDatabase,
  quarantineOfflineCache,
  releaseOfflineCacheQuarantine,
} from './offline/db';
import { useSyncStore } from './offline/sync-store';
import { SESSION_KEY } from './storage';
import { withTimeout, isTimeoutError } from './with-timeout';

/**
 * How long we wait for the offline cache to empty before giving up and letting
 * authentication proceed.
 *
 * 4s is chosen against what the slow-but-healthy case actually costs. The work
 * being timed is `openDB` (single-digit ms warm; slowest on a cold open that
 * runs the v3 upgrade) plus nine `clear()` calls, which on cheap eMMC in a
 * low-end field Android can run into hundreds of ms when `pendingUploads`
 * holds photo blobs. A 1s budget would fire on legitimately slow devices, and
 * every needless quarantine costs that user their offline cache for the whole
 * session — a real loss for someone about to drive out of coverage. 10s would
 * respect the purge but is dead time in front of two network round-trips
 * (`/auth/login`, then `/auth/me`) while the user watches a spinner, which is
 * the complaint we are fixing.
 *
 * 4s clears the plausible slow-device ceiling with margin while keeping the
 * worst added latency inside what reads as "it's working" rather than "it's
 * stuck". The asymmetry that makes this safe to tune for latency rather than
 * paranoia: firing early does not silently continue against a readable stale
 * cache — it quarantines. A false positive costs offline caching, never tenant
 * isolation.
 */
export const CACHE_PURGE_TIMEOUT_MS = 4000;

/**
 * Budget for the recovery `deleteDatabase`. Entirely off the login path — it
 * runs unawaited — so this only bounds how long we keep trying before giving
 * up and leaving the cache quarantined for the session.
 *
 * Defined in `offline/db` now that `getDB`'s own recovery needs the same
 * budget; re-exported here so existing importers of this name keep working.
 */
export { CACHE_DELETE_TIMEOUT_MS };

export type CachePurgeOutcome = 'cleared' | 'degraded';

/**
 * Tenant-scoped localStorage keys that must be purged on logout / tenant switch.
 * Non-tenant keys (fieldiq-theme, sidebarSection_*) are intentionally kept.
 */
const TENANT_LS_KEYS = [
  'fieldiq-auth',            // Zustand authStore persist
  'fieldiq_offline_queue',   // legacy offline queue fallback
  'chat_last_checked',       // per-user chat timestamp
  // The profile AuthContext opens from in a dead zone. Imported rather than
  // spelled again: a key written under one name and purged under another is
  // how the previous tenant's identity survives a sign-out. On native the
  // Preferences copy is cleared by `storage.setToken`, which every sign-in
  // passes through.
  SESSION_KEY,
];

/**
 * Empty the IndexedDB offline stores, bounded in time.
 *
 * Never rejects and never hangs. Returns 'cleared' only when the stores are
 * provably empty; anything else returns 'degraded' and leaves the cache
 * quarantined so the unpurged rows stay unreachable.
 */
async function purgeOfflineCache(): Promise<CachePurgeOutcome> {
  // Held separately from the raced promise: on timeout the underlying work is
  // still running (IndexedDB requests cannot be cancelled), and if it does
  // eventually finish we want to notice and lift the quarantine.
  const pending = clearAllStores();

  try {
    await withTimeout(pending, CACHE_PURGE_TIMEOUT_MS, 'clearAllStores');
    return 'cleared';
  } catch (err) {
    // Both branches land here on purpose. A timeout means we do not know
    // whether the stores emptied; a rejection means we know they did not.
    // Either way the previous tenant's rows may still be on disk, so either
    // way the cache is sealed.
    const reason = isTimeoutError(err)
      ? `purge exceeded ${CACHE_PURGE_TIMEOUT_MS}ms — offline database is not responding`
      : `purge failed — ${(err as any)?.message || 'unknown error'}`;

    console.error(`[tenant-cleanup] ${reason}. Sealing offline cache and continuing with sign-in.`);
    quarantineOfflineCache(reason);

    // Recovery runs in the background. Login does not wait on it.
    void recoverOfflineCache(pending);

    return 'degraded';
  }
}

/**
 * Try to get back to a clean cache after a failed purge, without blocking
 * anyone. Two ways out, whichever lands first:
 *
 *  1. The original purge finally completes — the stores are empty after all.
 *  2. The database is deleted outright — next `getDB()` recreates it empty.
 *
 * If neither works the quarantine simply stays for the rest of the session.
 */
async function recoverOfflineCache(pending: Promise<void>): Promise<void> {
  try {
    const deleted = await deleteOfflineDatabase(CACHE_DELETE_TIMEOUT_MS);
    if (deleted) {
      console.info('[tenant-cleanup] Offline database deleted; cache is clean again.');
      releaseOfflineCacheQuarantine();
      return;
    }
  } catch {
    // deleteOfflineDatabase already swallows; belt and braces.
  }

  // Delete was blocked (almost certainly by the connection the stuck purge is
  // holding). If that purge ever finishes, the stores are empty and the
  // quarantine can lift.
  try {
    await pending;
    console.info('[tenant-cleanup] Stalled purge completed late; cache is clean again.');
    releaseOfflineCacheQuarantine();
  } catch {
    console.warn('[tenant-cleanup] Offline cache could not be cleared; it stays sealed for this session.');
  }
}

/**
 * Hard-purge ALL client-side tenant data.
 * Called on logout and (belt-and-suspenders) before a new login session starts.
 *
 * Guaranteed to settle: no step here can leave the caller pending forever.
 */
export async function purgeTenantData(queryClient: QueryClient): Promise<CachePurgeOutcome> {
  // 1. React Query — drop every cached query and mutation
  queryClient.clear();

  // 2. IndexedDB offline stores (tickets, jobs, equipment, syncQueue, etc.)
  const outcome = await purgeOfflineCache();

  // 3. Zustand sync store — reset in-memory counters
  const syncState = useSyncStore.getState();
  syncState.setPendingMutations(0);
  syncState.setPendingUploads(0);
  syncState.setFailedMutations(0);
  syncState.setFailedUploads(0);
  syncState.setConflicts(0);
  syncState.setError(null);
  syncState.setCacheDegraded(outcome === 'degraded');

  // 4. Tenant-scoped localStorage keys
  for (const key of TENANT_LS_KEYS) {
    try { localStorage.removeItem(key); } catch { /* SSR / restricted */ }
  }

  // 5. sessionStorage — wipe entirely (list-state, filters, search terms)
  try { sessionStorage.clear(); } catch { /* SSR / restricted */ }

  // 6. Service worker caches (photos, static assets)
  // postMessage is fire-and-forget and deliberately not awaited — awaiting a
  // reply from a worker that may be dead is another way to hang login.
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHES' });
  }

  return outcome;
}
